import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  ChevronDown,
  HelpCircle,
  Phone,
  Mail,
  MessageSquare,
  ShieldAlert,
  MapPin,
  ArrowRight
} from 'lucide-react';
import ScrollToTop from '../components/Common/ScrollToTop';

const FAQ: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedIndex, setExpandedIndex] = useState<string | null>(null);

  const faqCategories = [
    {
      id: 'general',
      title: 'General Questions',
      icon: <HelpCircle className="w-5 h-5" />,
      faqs: [
        {
          question: 'What makes SightTrail Safaris different from other companies?',
          answer: 'We specialize in authentic East African wildlife encounters combined with deep cultural immersion. Our professional guides share their profound knowledge of the Great Migration, diverse ecosystems, and local heritage. We focus on sustainable tourism across Kenya and Tanzania.'
        },
        {
          question: 'What is included in the safari price?',
          answer: 'Our safari prices include professional East African guides, all game drives, park entry fees, accommodation, meals, airport transfers, and 24/7 emergency support.'
        },
        {
          question: 'How many people are in a safari group?',
          answer: 'We keep our groups small for a more intimate experience. Most safaris have 6-8 people maximum, ensuring personalized attention and optimal wildlife viewing across East Africa.'
        }
      ]
    },
    {
      id: 'booking',
      title: 'Booking & Payments',
      icon: <MapPin className="w-5 h-5" />,
      faqs: [
        {
          question: 'How far in advance should I book my safari?',
          answer: 'We recommend booking 3-6 months in advance. For the Great Migration viewing in Masai Mara or Serengeti, we suggest booking 6-12 months ahead.'
        },
        {
          question: 'What payment methods do you accept?',
          answer: 'We accept all major credit cards, bank transfers, and regional payment methods like M-Pesa. We require a 30% deposit to confirm your booking.'
        }
      ]
    },
    {
      id: 'safety',
      title: 'Safety & Health',
      icon: <ShieldAlert className="w-5 h-5" />,
      faqs: [
        {
          question: 'Is it safe to travel to East Africa?',
          answer: 'Yes, our safari destinations are safe for global travelers. We work with experienced local teams, stay in secure accommodations, and monitor safety conditions 24/7.'
        }
      ]
    }
  ];

  const contactMethods = [
    {
      icon: <Phone className="w-6 h-6" />,
      title: 'Call Us',
      contact: '+254 708 560 665',
      desc: '24/7 Global Support'
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: 'Email Us',
      contact: 'sighttrailsafaris@gmail.com',
      desc: 'Response within 24 hours'
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: 'WhatsApp',
      contact: '+254 708 560 665',
      desc: 'Real-time assistance'
    }
  ];

  const toggleFaq = (id: string) => {
    setExpandedIndex(expandedIndex === id ? null : id);
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
        staggerChildren: 0.1
      }
    }
  } as const;

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' }
    }
  } as const;

  return (
    <div className="min-h-screen bg-[#F8FBFF] font-sans selection:bg-primary-100 italic-shadows">
      {/* Search & Hero Section */}
      <div className="relative pt-32 pb-24 px-6 overflow-hidden">
        {/* Subtle Background Pattern */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="faq-grid" width="60" height="60" patternUnits="userSpaceOnUse">
                <path d="M 60 0 L 0 0 0 60" fill="none" stroke="currentColor" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#faq-grid)" />
          </svg>
        </div>

        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="max-w-4xl mx-auto text-center relative z-10"
        >
          <motion.div variants={itemVariants} className="inline-flex items-center px-4 py-2 rounded-full bg-primary-50 border border-primary-100 mb-6">
            <HelpCircle className="w-4 h-4 text-primary-600 mr-2" />
            <span className="text-[10px] font-bold text-primary-600 uppercase tracking-widest luxury-font">Knowledge Base</span>
          </motion.div>
          
          <motion.h1 variants={itemVariants} className="text-4xl md:text-6xl font-extrabold text-primary-900 tracking-tight font-serif mb-6">
            Frequently Asked <span className="text-primary-600">Questions</span>
          </motion.h1>
          
          <motion.p variants={itemVariants} className="text-lg text-neutral-500 font-medium max-w-2xl mx-auto mb-12">
            Everything you need to know about our authentic East African safaris and wildlife experiences.
          </motion.p>

          <motion.div variants={itemVariants} className="relative max-w-xl mx-auto">
            <div className="absolute inset-y-0 left-0 pl-6 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-neutral-300" />
            </div>
            <input
              type="text"
              placeholder="How can we help you today?"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-14 pr-6 py-5 bg-white border border-blue-50 rounded-[32px] shadow-[0_20px_50px_-12px_rgba(42,126,206,0.08)] text-primary-900 placeholder:text-neutral-300 focus:outline-none focus:ring-4 focus:ring-primary-500/5 focus:border-primary-400 transition-all text-base"
            />
          </motion.div>
        </motion.div>
      </div>

      <div className="max-w-5xl mx-auto px-6 pb-32">
        <div className="space-y-16">
          {faqCategories.map((category) => (
            <motion.div 
              key={category.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="flex items-center space-x-3 px-2">
                <div className="p-2 bg-primary-50 rounded-xl text-primary-600">
                  {category.icon}
                </div>
                <h2 className="text-xl font-bold text-primary-900 tracking-tight">{category.title}</h2>
              </div>

              <div className="space-y-3">
                {category.faqs.map((faq, idx) => {
                  const faqId = `${category.id}-${idx}`;
                  const isExpanded = expandedIndex === faqId;
                  
                  return (
                    <div 
                      key={idx}
                      className={`group transition-all duration-300 border ${isExpanded ? 'bg-white border-primary-200 shadow-[0_16px_32px_-8px_rgba(42,126,206,0.06)] scale-[1.01]' : 'bg-white/50 border-blue-50 hover:border-primary-100 hover:bg-white'} rounded-[24px] overflow-hidden`}
                    >
                      <button
                        onClick={() => toggleFaq(faqId)}
                        className="w-full text-left px-8 py-6 flex items-center justify-between group"
                      >
                        <h3 className={`text-lg font-bold transition-colors ${isExpanded ? 'text-primary-700' : 'text-primary-900 group-hover:text-primary-700'}`}>
                          {faq.question}
                        </h3>
                        <div className={`p-2 rounded-full transition-all duration-300 ${isExpanded ? 'bg-primary-600 text-white rotate-180' : 'bg-primary-50 text-primary-400 group-hover:bg-primary-100'}`}>
                          <ChevronDown className="w-4 h-4" />
                        </div>
                      </button>
                      
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                          >
                            <div className="px-8 pb-8 pt-0 text-neutral-500 leading-relaxed font-medium">
                              <p>{faq.answer}</p>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Contact Support Cards */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-32 p-12 rounded-[48px] bg-white border border-blue-50 shadow-[0_40px_80px_-20px_rgba(42,126,206,0.1)] text-center relative overflow-hidden"
        >
          {/* Accent decoration */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-1.5 bg-primary-600 rounded-b-full" />
          
          <div className="relative z-10">
            <h2 className="text-3xl font-extrabold text-primary-900 font-serif mb-3">Still have questions?</h2>
            <p className="text-neutral-500 font-medium mb-12">Our safari experts are ready to help you plan your dream journey.</p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {contactMethods.map((method, i) => (
                <div key={i} className="group p-6 rounded-3xl bg-neutral-50/50 hover:bg-white hover:shadow-xl hover:shadow-primary-600/5 border border-transparent hover:border-primary-100 transition-all duration-500">
                  <div className="w-14 h-14 bg-white rounded-2xl shadow-sm border border-blue-50 flex items-center justify-center mx-auto mb-6 text-primary-600 transition-transform group-hover:scale-110">
                    {method.icon}
                  </div>
                  <h3 className="text-sm font-bold text-primary-900 uppercase tracking-widest mb-2 luxury-font">{method.title}</h3>
                  <div className="text-primary-700 font-extrabold mb-1">{method.contact}</div>
                  <div className="text-[10px] text-neutral-400 font-bold uppercase">{method.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Professional Support Image CTA */}
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-16 group relative rounded-[32px] overflow-hidden aspect-[21/9] md:aspect-[3/1] shadow-2xl"
        >
          <img
            src="https://images.unsplash.com/photo-1516426122078-c23e76319801?q=80&w=2668&auto=format&fit=crop"
            alt="Safari Planning"
            className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/40 to-transparent flex items-center px-12">
            <div className="max-w-md text-white">
              <h3 className="text-2xl md:text-3xl font-extrabold font-serif mb-4 leading-tight">Start your authentic Maasai journey today.</h3>
              <button className="flex items-center text-sm font-bold uppercase tracking-[0.2em] group/btn">
                Plan your Trip
                <ArrowRight className="w-4 h-4 ml-3 group-hover/btn:translate-x-2 transition-transform" />
              </button>
            </div>
          </div>
        </motion.div>
      </div>

      <ScrollToTop />
    </div>
  );
};

export default FAQ;
